import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';

import { Observable } from 'rxjs';
import { LocalStoreService } from './localstore.service';
import { SpotifyHardcodedOptions, SpotifyLimits } from '../models/consts';
declare global {
  interface Window {
    spotifyCallback: any;
  }
}
@Injectable({
  providedIn: 'root'
})
export class SpotifyService {
  private apiUrl = 'https://api.spotify.com/v1';
  private nextPlaylistsURL: string;
  private tokenUrl = 'https://accounts.spotify.com/api/token';
  private accessToken: string;

  constructor(
    private http: HttpClient,
    private localstoreService: LocalStoreService
  ) { }

  resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
    // Full URL debug (optional)
    // console.log('spotifyCallback URL =', window.location.href);

    // Spotify sends ?code=...&state=... in the query string
    const params = new URLSearchParams(window.location.search.substring(1));

    if (window.opener && window.opener.spotifyCallback) {
      window.opener.spotifyCallback(params);
    }

    return null;
  }

  getAccessToken(): Promise<string> {
    const encodedCredentials = btoa(`${environment.EXTERNAL_LOG.ID}:${environment.EXTERNAL_LOG.UUID}`);
    const headers = new HttpHeaders({
      Authorization: `Basic ${encodedCredentials}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');

    return this.http
      .post(this.tokenUrl, body.toString(), { headers })
      .toPromise()
      .then((response: any) => {
        this.accessToken = response.access_token;
        return response.access_token;
      })
      .catch((error) => {
        console.error('Error getting Spotify token:', error);
        throw error;
      });
  }


  authorize(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const clientId = environment.SPOTIFY.CLIENT_ID;
        const redirectUri = `${window.location.origin}/spotifyCallback`;
        const scopes = ['playlist-read-private'].join(' ');

        // Generate PKCE code verifier + challenge
        const verifier = this.generateVerifier();
        const challenge = await this.generateChallenge(verifier);

        // Store verifier for token exchange later
        this.localstoreService.setItemStringified('spotify_pkce_verifier', verifier);

        const authUrl =
          `https://accounts.spotify.com/authorize?client_id=${encodeURIComponent(clientId)}` +
          `&response_type=code` +
          `&redirect_uri=${encodeURIComponent(redirectUri)}` +
          `&scope=${encodeURIComponent(scopes)}` +
          `&code_challenge_method=S256` +
          `&code_challenge=${encodeURIComponent(challenge)}`;

        const authPopup = window.open(
          authUrl,
          'Login Spotify',
          'width=800,height=600'
        );

        window.spotifyCallback = async (searchParams: URLSearchParams) => {
          try {
            const code = searchParams.get('code');
            if (!code) {
              throw new Error('No authorization code received');
            }

            const storedVerifier = this.localstoreService.getItemParsed('spotify_pkce_verifier');

            if (!storedVerifier) {
              throw new Error('No PKCE verifier stored');
            }

            // Exchange code for an access token
            const body = new URLSearchParams({
              client_id: clientId,
              grant_type: 'authorization_code',
              code,
              redirect_uri: redirectUri,
              code_verifier: storedVerifier
            });

            const response = await fetch('https://accounts.spotify.com/api/token', {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body,
            });

            const tokenData = await response.json();

            if (!tokenData.access_token) {
              console.error('Token exchange failed:', tokenData);
              throw new Error('Token exchange failed');
            }

            // Store token and expiry as before
            this.localstoreService.setItemStringified('spotify_token', tokenData.access_token);
            this.localstoreService.setItemStringified(
              'spotify_token_expire_date',
              Date.now() + tokenData.expires_in * 1000
            );

            delete window.spotifyCallback;
            if (authPopup) {
              authPopup.close();
            }
            resolve(undefined);
          } catch (err) {
            console.error('Error in spotifyCallback:', err);
            if (authPopup) {
              authPopup.close();
            }
            reject(err);
          }
        };
      } catch (err) {
        reject(err);
      }
    });
  }

  isAuthorized() {
    const token = this.localstoreService.getItemParsed('spotify_token');
    const expiredDate = this.localstoreService.getItemParsed('spotify_token_expire_date');
    const isTokenExpired = expiredDate ? Date.now() >= expiredDate : true;

    return token && !isTokenExpired;
  }

  searchTracks(query: string, offset: number, limit: number): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    return this.http.get(`${this.apiUrl}/search`, {
      headers,
      params: { q: query, type: 'track', limit: limit, offset: offset, market: 'AU' },
    });
  }

  getSeveralTracks(trackIds: string[]): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    const idsParam = trackIds.join(',');
    return this.http.get(`${this.apiUrl}/tracks`, {
      headers,
      params: { ids: idsParam }
    });
  }

  getAlbumById(albumId: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    return this.http.get(`${this.apiUrl}/albums/${albumId}`, { headers });
  }

  getAlbumTracks(albumId: string, offset: number, limit: number): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    return this.http.get(`${this.apiUrl}/albums/${albumId}/tracks`, {
      headers,
      params: { offset: offset.toString(), limit: limit.toString(), market: 'AU' }
    });
  }

  searchArtistAlbums(artistName: string, offset: number, limit: number): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
    const query = `artist:"${artistName}"`;
    return this.http.get(`${this.apiUrl}/search`, {
      headers,
      params: {
        q: query,
        type: 'album',
        limit: limit,
        offset: offset,
        market: 'AU'
      },
    });
  }

  getSpotifyPlaylists(query: string = '', offset: number = 0, limit: number = SpotifyLimits.PLAYLISTS_LIMIT): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      this.http.get(`${environment.SPOTIFY.API_URL}me/playlists?${params}`, this.getHttpOptions())
        .subscribe(
          (data: any) => {
            if (data.items && data.items.length > 0) {
              this.nextPlaylistsURL = data.next;

              // Server-side filter by query if provided
              const filteredPlaylists = data.items.filter((playlist: any) =>
                playlist.name.toLowerCase().includes(query.toLowerCase())
              );

              resolve(filteredPlaylists);
            } else {
              resolve([]);
            }
          },
          (error) => {
            if (error && error.status === 401) {
              this.localstoreService.removeItem('spotify_token');
              this.localstoreService.removeItem('spotify_token_expire_date');
              reject();
            }

            reject(error);
          }
        );
    });
  }


  // getSpotifyPlaylists(): Promise<any> {
  //   return new Promise<any>((resolve, reject) => {
  //     this.http.get(`${environment.SPOTIFY.API_URL}me/playlists?limit=${SpotifyLimits.PLAYLISTS_LIMIT}`, this.getHttpOptions())
  //       .subscribe(
  //         (data: any) => {
  //           if (data.items && data.items.length > 0) {
  //             this.nextPlaylistsURL = data.next;

  //             resolve(data.items);
  //           }
  //           resolve([]);
  //         },
  //         (error) => {
  //           if (error && error.status === 401) {
  //             this.localstoreService.removeItem('spotify_token');
  //             this.localstoreService.removeItem('spotify_token_expire_date');
  //             reject();
  //           }

  //           reject(error);
  //         }
  //       );
  //   });
  // }

  getMoreSpotifyPlaylists(): Promise<any> {
    if (this.nextPlaylistsURL) {
      return new Promise<any>((resolve, reject) => {
        this.http.get(this.nextPlaylistsURL, this.getHttpOptions())
          .subscribe(
            (data: any) => {
              if (data.items && data.items.length > 0) {
                this.nextPlaylistsURL = data.next;

                resolve(data.items);
              }
              resolve([]);
            },
            (error) => {
              if (error && error.status === 401) {
                this.localstoreService.removeItem('spotify_token');
                this.localstoreService.removeItem('spotify_token_expire_date');
              }

              reject(error);
            }
          );
      });
    }

    return Promise.resolve([]);
  }

  getSpotifyPlaylistTracks(url, offset, limit): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.http.get(`${url}?offset=${offset}&limit=${limit}`, this.getHttpOptions())
        .subscribe(
          (data: any) => {
            if (data.items && data.items.length > 0) {
              const tracks = data.items.filter((item) => item.track);

              resolve(tracks);
            }

            resolve([]);
          },
          (error) => {
            console.log(error);
            reject(error);
          }
        );
    });
  }

  getSpotifyRecommended(tracksSeed: string, genreSeed?: string, artistsSeed?: string): Observable<any> {
    return this.http.get(`${environment.SPOTIFY.API_URL}recommendations?`
      + `limit=${SpotifyLimits.RECOMMENDED_TRACKS_LIMIT}&market=${SpotifyHardcodedOptions.market}`
      + `&seed_tracks=${tracksSeed}`, this.getHttpOptions());
  }


  // correct type: Promise<SpotifyApi.SearchResponse>
  async searchSpotify(query: string, types: string[], options: Record<string, any>): Promise<any> {
    const params = new URLSearchParams({
      ...options,
      type: types.join(','),
      q: query,
    });

    return this.http.get(`${environment.SPOTIFY.API_URL}search?${params}`, this.getHttpOptions()).toPromise();;
  }

  // correct type: Promise<SpotifyApi.PlaylistTrackResponse>
  async getPlaylistTracks(playlistId: string): Promise<any> {
    return this.http.get(`${environment.SPOTIFY.API_URL}playlists/${playlistId}/tracks`, this.getHttpOptions()).toPromise();
  }

  getHttpOptions() {
    const token = this.localstoreService.getItemParsed('spotify_token');

    return {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + token
      })
    };
  }

  // Generate a random string for PKCE verifier
  private generateVerifier(length = 128): string {
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return result;
  }

  // Convert verifier to a SHA256 challenge
  private async generateChallenge(verifier: string): Promise<string> {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }

}
